import React from "react";
import { MapPin, ArrowRight } from "lucide-react";
import type { BackendOrder } from "./types";
import { DEFAULT_PRODUCT_IMAGE } from "../../utils/imageUtils";
import { useNavigate } from "react-router-dom";

interface OrderCardProps {
  order: BackendOrder;
  orders: BackendOrder[];
}

const OrderCard: React.FC<OrderCardProps> = ({ order, orders }) => {
  const getStatusColor = (stageName: string) => {
    let normalized = stageName.toLowerCase();
    if (normalized === "confirmed") normalized = "pending";
    if (normalized === "shipping") normalized = "shipped";
    if (normalized === "completed") normalized = "delivered";

    const colors = {
      pending: "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20",
      shipped: "bg-blue-500/10 text-blue-400 border border-blue-500/20",
      delivered: "bg-green-500/10 text-green-400 border border-green-500/20",
    };
    return (
      colors[normalized as keyof typeof colors] ||
      "bg-neutral-800 text-neutral-400 border border-neutral-700"
    );
  };

  const formatCurrency = (amount: number | undefined | null) => {
    return `$${(amount ?? 0).toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const navigate = useNavigate();

  const displayStatus = (() => {
    const s = (order.stage_name || "").toLowerCase();
    if (s === "confirmed") return "Pending";
    if (s === "shipping") return "Shipped";
    if (s === "completed") return "Delivered";
    return order.stage_name || "Unknown";
  })();

  return (
    <div className="p-6 hover:bg-neutral-800/50 transition-colors group border-l-4 border-l-transparent hover:border-l-primary">
      {/* Order Header */}
      <div className="flex flex-col sm:flex-row items-start justify-between mb-6 gap-4 sm:gap-0">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-bold text-white group-hover:text-primary transition-colors">
              Order #{order.id}
            </h3>
            <span
              className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                order.stage_name || "",
              )}`}
            >
              {displayStatus}
            </span>
          </div>
          <p className="text-sm text-neutral-400 flex items-center gap-2">
            <span className="text-white font-medium">
              {formatDate(order.created_at)}
            </span>
            <span className="w-1 h-1 bg-neutral-600 rounded-full"></span>
            <span>
              {order.products.length}{" "}
              {order.products.length === 1 ? "item" : "items"}
            </span>
          </p>
        </div>
        <div className="text-left sm:text-right">
          <p className="text-xs text-neutral-500 mb-1 uppercase tracking-wide font-semibold">
            Total Amount
          </p>
          <p className="text-xl sm:text-2xl font-bold text-white group-hover:text-primary transition-colors">
            {formatCurrency(order.total_usd_price)}
          </p>
        </div>
      </div>

      {/* Address */}
      {order.address_value && (
        <div className="flex items-start gap-3 text-sm text-neutral-300 mb-6 p-4 bg-neutral-800/50 rounded-xl border border-neutral-800/50 hover:border-neutral-700 transition-colors">
          <div className="w-8 h-8 rounded-full bg-neutral-800 flex items-center justify-center shrink-0">
            <MapPin className="w-4 h-4 text-primary" />
          </div>
          <div className="mt-1">
            <span className="font-semibold text-white block mb-0.5">
              Shipping Address
            </span>
            <span className="text-neutral-400">
              {order.address_value.street}, {order.address_value.city}
              {order.address_value.building &&
                `, ${order.address_value.building}`}
            </span>
          </div>
        </div>
      )}

      {/* Product Items */}
      <div className="space-y-4">
        {order.products.map((item, index) => {
          const isBackendProduct = orders.length > 0;
          const productId = isBackendProduct
            ? (item as any).product_id
            : (item as any).id;
          const productName = isBackendProduct
            ? (item as any).product_name
            : (item as any).name;
          const productImage = isBackendProduct
            ? (item as any).image_paths?.[0]
            : (item as any).image;
          const productPrice = isBackendProduct
            ? (item as any).discounted_price
            : (item as any).price;
          const productQuantity = item.quantity;
          const discountRate = isBackendProduct
            ? (item as any).discount_rate
            : 0;

          return (
            <div
              key={`${order.id}-product-${productId || index}`}
              onClick={() => navigate(`/product/${productId}`)}
              className="flex items-center gap-4 p-4 bg-neutral-800/30 border border-neutral-800 rounded-xl hover:bg-neutral-800 hover:border-primary/30 transition-all cursor-pointer group/item"
            >
              {/* Image */}
              <div className="relative shrink-0">
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-neutral-800">
                  <img
                    src={
                      isBackendProduct && productImage
                        ? `https://gzvhulvbgyymgjgdalpa.supabase.co/storage/v1/object/public/publicimages/product_images/${productImage}`
                        : productImage || DEFAULT_PRODUCT_IMAGE
                    }
                    alt={productName || "Product"}
                    className="w-full h-full object-cover group-hover/item:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = DEFAULT_PRODUCT_IMAGE;
                    }}
                  />
                </div>
                {discountRate > 0 && (
                  <span className="absolute -top-2 -right-2 bg-primary text-black text-[10px] px-2 py-0.5 rounded-full font-bold shadow-lg">
                    -{Math.round(discountRate * 100)}%
                  </span>
                )}
              </div>

              {/* Product Info */}
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start">
                  <div>
                    <h5 className="font-bold text-white mb-1 line-clamp-1 group-hover/item:text-primary transition-colors text-base">
                      {productName || "Unknown Product"}
                    </h5>
                    <p className="text-xs text-neutral-500 mb-2 font-medium bg-neutral-800 inline-block px-2 py-1 rounded">
                      Qty: {productQuantity}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-1">
                  <p className="text-lg font-bold text-white">
                    {formatCurrency(productPrice * (productQuantity || 1))}
                  </p>
                  <div className="w-8 h-8 rounded-full bg-neutral-800 flex items-center justify-center opacity-0 group-hover/item:opacity-100 transform translate-x-2 group-hover/item:translate-x-0 transition-all duration-300">
                    <ArrowRight className="w-4 h-4 text-primary" />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderCard;
